import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power, RotateCcw, Trash2, UserRound } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  Button,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Row,
  Spinner,
  Tabs,
} from '../../components/ui';
import { api } from '../../lib/api';
import {
  formatCredits,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatSchedule,
  humanize,
} from '../../lib/format';
import { useT } from '../../lib/i18n';
import {
  RecordLink,
  RecordNav,
  useOnwardTrail,
  useTrail,
} from '../../lib/navigation';
import { errorMessage, useToast } from '../../lib/toast';
import type { AdminAlarm } from '../../lib/types';
import { AlarmEventsTab } from './AlarmEvents';

type AlarmTab = 'detail' | 'events';

/**
 * One alarm, and the two records either side of it.
 *
 * The alarm is the hinge of the whole monitoring walk: the account that owns
 * it is one way out, the events it produced are the other, and both are in the
 * same place on every sheet in this console.
 */
export function AlarmDrawer({
  alarmId,
  onClose,
}: {
  alarmId: string;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const trail = useTrail();
  const onward = useOnwardTrail('alarm', alarmId);
  const [tab, setTab] = useState<AlarmTab>('detail');
  const [dialog, setDialog] = useState<'toggle' | 'delete' | 'restore' | null>(
    null,
  );
  const [edit, setEdit] = useState<{
    hour: string;
    minute: string;
    label: string;
  } | null>(null);

  const alarm = useQuery({
    queryKey: ['alarm', alarmId],
    queryFn: () => api.get<AdminAlarm>(`/api/alarms/${alarmId}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['alarm', alarmId] });
    void queryClient.invalidateQueries({ queryKey: ['alarms'] });
    void queryClient.invalidateQueries({ queryKey: ['user-alarms'] });
  };

  const act = useMutation({
    mutationFn: async ({ kind, reason }: { kind: string; reason: string }) => {
      const current = alarm.data;
      if (!current) return;

      if (kind === 'delete') {
        return api.delete(`/api/alarms/${alarmId}`, { reason });
      }
      if (kind === 'restore') {
        return api.post(`/api/alarms/${alarmId}/restore`, { reason });
      }
      if (kind === 'edit' && edit) {
        return api.patch(`/api/alarms/${alarmId}`, {
          label: edit.label,
          hour: Number(edit.hour),
          minute: Number(edit.minute),
          reason,
        });
      }
      return api.patch(`/api/alarms/${alarmId}`, {
        enabled: !current.enabled,
        reason,
      });
    },
    onSuccess: (_data, variables) => {
      toast.success(t('common.done'));
      setDialog(null);
      setEdit(null);
      refresh();
      if (variables.kind === 'delete') onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        alarm.data
          ? `${alarm.data.time} · ${alarm.data.label || t('alarms.drawerFallback')}`
          : t('common.loadingEllipsis')
      }
    >
      {alarm.isPending && <Spinner />}
      {alarm.isError && <ErrorNote error={alarm.error} />}

      {alarm.data && (
        <div className="space-y-5">
          <RecordNav trail={trail}>
            <RecordLink
              to={{ kind: 'user', id: alarm.data.userId }}
              trail={onward}
              icon={<UserRound size={15} />}
            >
              {t('alarms.viewUser')}
            </RecordLink>
          </RecordNav>

          <Tabs
            label={t('alarms.tabsLabel')}
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'detail', label: t('alarms.tab.detail') },
              { id: 'events', label: t('alarms.tab.events') },
            ]}
          />

          {tab === 'events' ? (
            <AlarmEventsTab alarm={alarm.data} />
          ) : (
            <AlarmDetail
              alarm={alarm.data}
              edit={edit}
              onEdit={setEdit}
              onDialog={setDialog}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === 'delete'
            ? t('alarms.dialog.delete.title')
            : dialog === 'restore'
              ? t('alarms.dialog.restore.title')
              : edit
                ? t('alarms.dialog.save.title')
                : alarm.data?.enabled
                  ? t('alarms.dialog.off.title')
                  : t('alarms.dialog.on.title')
        }
        description={
          dialog === 'delete'
            ? t('alarms.dialog.delete.description')
            : t('alarms.dialog.other.description')
        }
        destructive={dialog === 'delete'}
        loading={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          act.mutate({ kind: edit ? 'edit' : (dialog ?? 'toggle'), reason })
        }
      />
    </Drawer>
  );
}

type EditState = { hour: string; minute: string; label: string } | null;

function AlarmDetail({
  alarm,
  edit,
  onEdit,
  onDialog,
}: {
  alarm: AdminAlarm;
  edit: EditState;
  onEdit: (edit: EditState) => void;
  onDialog: (dialog: 'toggle' | 'delete' | 'restore') => void;
}) {
  const t = useT();

  return (
    <div className="space-y-5">
      {alarm.heldAmount > 0 && (
        <div className="rounded-lg border border-brand/25 bg-brand-soft px-3 py-2 text-sm text-brand">
          {t('alarms.holding', { amount: formatCredits(alarm.heldAmount) })}
        </div>
      )}

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('alarms.section.schedule')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('alarms.row.ringsAt')}>{alarm.time}</Row>
          <Row label={t('alarms.row.repeats')}>
            {formatSchedule(alarm.recurrence.label)}
          </Row>
          <Row label={t('alarms.row.perWeek')}>{alarm.weeklyOccurrences}</Row>
          <Row label={t('alarms.row.tzOffset')}>
            {alarm.timezoneOffsetMinutes === null
              ? t('alarms.notReported')
              : t('alarms.minutesShort', {
                  count: alarm.timezoneOffsetMinutes,
                })}
          </Row>
          <Row label={t('alarms.row.enabled')}>
            {alarm.enabled ? (
              <Badge tone="good">{t('common.on')}</Badge>
            ) : (
              <Badge>{t('common.off')}</Badge>
            )}
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('alarms.section.challenge')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('alarms.row.kind')}>
            {humanize(alarm.challengeType)}
          </Row>
          <Row label={t('alarms.row.difficulty')}>
            {humanize(alarm.challengeDifficulty)}
          </Row>
          <Row label={t('alarms.row.rounds')}>{alarm.requiredRounds}</Row>
          <Row label={t('alarms.row.timeLimit')}>
            {alarm.rewardTimeoutSeconds}s
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('alarms.section.record')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('alarms.row.rang')}>
            {t('alarms.rangTimes', { count: formatNumber(alarm.ringCount) })}
          </Row>
          <Row label={t('alarms.row.dismissed')}>
            {t('alarms.rangTimes', { count: formatNumber(alarm.dismissCount) })}
          </Row>
          <Row label={t('alarms.row.challengesWon')}>
            {formatNumber(alarm.rewardEarnedCount)}
          </Row>
          <Row label={t('alarms.row.challengesMissed')}>
            {formatNumber(alarm.rewardMissedCount)}
          </Row>
          <Row label={t('alarms.row.wakeRate')}>
            {alarm.wakeRate === null
              ? t('users.nothingSettled')
              : formatPercent(alarm.wakeRate)}
          </Row>
          <Row label={t('alarms.row.lifetimeCost')}>
            {formatCredits(alarm.forfeitedAmount)}
          </Row>
          <Row label={t('alarms.row.lastRang')}>
            {formatDateTime(alarm.lastRingAt)}
          </Row>
          <Row label={t('alarms.row.lastDismissed')}>
            {formatDateTime(alarm.lastDismissedAt)}
          </Row>
          <Row label={t('alarms.row.lastEvent')}>
            {formatDateTime(alarm.lastEventAt)}
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('alarms.section.identity')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('alarms.row.alarmId')} mono>
            <IdChip id={alarm.id} />
          </Row>
          <Row label={t('alarms.row.user')} mono>
            <IdChip id={alarm.userId} />
          </Row>
          <Row label={t('alarms.row.device')} mono>
            {alarm.deviceId ?? '—'}
          </Row>
          <Row label={t('alarms.row.clientId')}>{alarm.clientAlarmId}</Row>
          <Row label={t('alarms.row.created')}>
            {formatDateTime(alarm.createdAt)}
          </Row>
        </dl>
      </section>

      {edit ? (
        <section className="rounded-lg border border-line p-3">
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('alarms.changeTime')}
          </h3>
          <div className="grid grid-cols-3 gap-2">
            <Field label={t('alarms.hour')}>
              <Input
                type="number"
                min={0}
                max={23}
                value={edit.hour}
                onChange={(event) =>
                  onEdit({ ...edit, hour: event.target.value })
                }
              />
            </Field>
            <Field label={t('alarms.minute')}>
              <Input
                type="number"
                min={0}
                max={59}
                value={edit.minute}
                onChange={(event) =>
                  onEdit({ ...edit, minute: event.target.value })
                }
              />
            </Field>
            <Field label={t('alarms.label')} className="col-span-3">
              <Input
                value={edit.label}
                onChange={(event) =>
                  onEdit({ ...edit, label: event.target.value })
                }
              />
            </Field>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => onEdit(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => onDialog('toggle')}
            >
              {t('common.save')}
            </Button>
          </div>
        </section>
      ) : (
        <section className="flex flex-wrap gap-2 border-t border-line pt-4">
          <Button
            onClick={() =>
              onEdit({
                hour: String(alarm.hour),
                minute: String(alarm.minute),
                label: alarm.label,
              })
            }
          >
            {t('alarms.editTimeAndLabel')}
          </Button>
          <Button icon={<Power size={15} />} onClick={() => onDialog('toggle')}>
            {alarm.enabled ? t('alarms.switchOff') : t('alarms.switchOn')}
          </Button>
          {alarm.isDeleted ? (
            <Button
              icon={<RotateCcw size={15} />}
              onClick={() => onDialog('restore')}
            >
              {t('common.restore')}
            </Button>
          ) : (
            <Button
              variant="danger"
              icon={<Trash2 size={15} />}
              onClick={() => onDialog('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
        </section>
      )}
    </div>
  );
}
